import * as React from "react";
import {
  useUpdate,
  SimpleForm,
  SelectInput,
  SelectArrayInput,
  NumberInput,
  TextInput,
  required,
  minValue,
  useGetOne,
  BooleanInput,
  DateInput,
  FormDataConsumer,
} from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Stack,
  Alert,
  Grid,
} from "@mui/material";

import { EquipmentNameList } from "../common/consts/EquipmentNameList.const";
import { EquipmentSizeList } from "../common/consts/EquipmentSize.const";
import { EquipmentUnitList } from "../common/consts/RquipmentUnit.const";
import { WastesChoises } from "../common/consts/wasteList.const";
import {
  FrequencyChoices,
  MonthChoices,
  OrdinalChoices,
  WeekdayChoices,
} from "../interfaces/Equipment.interface";

export const EquipmentEditDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  id: string | null;
}> = ({ open, onClose, id }) => {
  const [update, { isLoading }] = useUpdate();
  const { data: record } = useGetOne(
    "equipments",
    { id: id! },
    { enabled: !!id }
  );

  if (!id || !record) return null;

  const defaults = {
    ...record,
    collectionPlan: {
      enabled: true,
      frequency: "WEEKLY",
      weekdays: ["MONDAY"],
      ordinal: "FIRST",
      monthlyWeekday: "MONDAY",
      weekOffset: 0,
      anchorMonth: 1,
      timezone: "Europe/Paris",
      notes: "",
      ...record.collectionPlan,
    },
  };

  const handleSubmit = async (values: any) => {
    await update(
      "equipments",
      { id, data: values, previousData: record },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Modifier l'équipement</DialogTitle>
      <DialogContent dividers>
        <SimpleForm
          defaultValues={defaults}
          onSubmit={handleSubmit}
          toolbar={false}
          mode="onChange"
        >
          <TextInput source="site" sx={{ display: "none" }} />

          <Alert color="info" sx={{ mb: 2 }}>
            Les champs ci-dessous mettent à jour l’équipement et sa
            planification
            <strong> dans la même requête</strong>.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <SelectInput
                source="name"
                label="Nom de l'équipement"
                validate={[required()]}
                choices={EquipmentNameList}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <SelectInput
                source="size"
                label="Taille de l'équipement"
                validate={[required()]}
                choices={EquipmentSizeList}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <SelectInput
                source="unit"
                label="Unité"
                validate={[required()]}
                choices={EquipmentUnitList}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <NumberInput
                source="quantity"
                label="Quantité d'équipement sur le site"
                validate={[required(), minValue(1)]}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SelectInput
                source="waste"
                label="Déchet"
                choices={WastesChoises}
                validate={[required()]}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <NumberInput
                source="density"
                label="Densité"
                validate={[required(), minValue(0)]}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mt: 3, mb: 1 }}>
                Planification de collecte (collectionPlan)
              </Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SelectInput
                source="collectionPlan.frequency"
                label="Fréquence"
                choices={FrequencyChoices}
                validate={[required()]}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormDataConsumer>
                {({ formData }) => {
                  const freq = formData?.collectionPlan?.frequency;

                  const isWeekly =
                    freq === "DAILY" ||
                    freq === "WEEKLY" ||
                    freq === "BIWEEKLY";

                  const isMonthlyFamily =
                    freq === "MONTHLY" ||
                    freq === "QUARTERLY" ||
                    freq === "ANNUALLY";

                  return (
                    <>
                      {/* DAILY/WEEKLY/BIWEEKLY => weekdays (+ weekOffset) */}
                      {isWeekly && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid size={{ xs: 12 }}>
                            <SelectArrayInput
                              source="collectionPlan.weekdays"
                              label="Jours"
                              choices={WeekdayChoices}
                              helperText="Choisissez un ou plusieurs jours de collecte."
                              validate={[required()]}
                              fullWidth
                            />
                          </Grid>
                          {freq === "BIWEEKLY" && (
                            <Grid size={{ xs: 12, md: 6 }}>
                              <SelectInput
                                source="collectionPlan.weekOffset"
                                label="Décalage de quinzaine"
                                choices={[
                                  { id: 0, name: "Semaine paire (offset 0)" },
                                  { id: 1, name: "Semaine impaire (offset 1)" },
                                ]}
                                fullWidth
                              />
                            </Grid>
                          )}
                        </Grid>
                      )}

                      {/* MONTHLY/QUARTERLY/ANNUALLY => ordinal + weekday (+ anchorMonth) */}
                      {isMonthlyFamily && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <SelectInput
                              source="collectionPlan.ordinal"
                              label="Ordre dans le mois"
                              choices={OrdinalChoices}
                              validate={[required()]}
                              fullWidth
                            />
                          </Grid>

                          {formData?.collectionPlan?.ordinal !== "ALL" && (
                            <Grid size={{ xs: 12, md: 6 }}>
                              <SelectInput
                                source="collectionPlan.monthlyWeekday"
                                label="Jour de la semaine"
                                choices={WeekdayChoices}
                                validate={[required()]}
                                fullWidth
                              />
                            </Grid>
                          )}

                          {(freq === "QUARTERLY" || freq === "ANNUALLY") && (
                            <Grid size={{ xs: 12, md: 6 }}>
                              <SelectInput
                                source="collectionPlan.anchorMonth"
                                label="Mois de départ"
                                choices={MonthChoices}
                                fullWidth
                              />
                            </Grid>
                          )}
                        </Grid>
                      )}

                      {/* Commun : dates / timezone / notes */}
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <DateInput
                            source="collectionPlan.startDate"
                            label="Début de validité"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <DateInput
                            source="collectionPlan.endDate"
                            label="Fin de validité"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextInput
                            source="collectionPlan.timezone"
                            label="Fuseau horaire"
                            defaultValue="Europe/Paris"
                            helperText="Ex: Europe/Paris"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextInput
                            source="collectionPlan.notes"
                            label="Notes"
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </>
                  );
                }}
              </FormDataConsumer>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <BooleanInput label="Actif" source="active" />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={isLoading} variant="contained">
              Enregistrer
            </Button>
          </Stack>
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};
